import { fa, toman, qty } from './fa';
import { soldPastZero } from './stock';
import type { CartLine } from './types';
import type { Totals } from './money';

/*
 * The basket, and the number the customer is about to be told.
 *
 * 428px, fixed, always on screen. The total is the largest thing on the till
 * for a reason: it is read aloud dozens of times an hour, and a cashier who has
 * to look for it reads it wrong.
 */

export function CartPanel({
  lines, totals, invoiceLabel, parkedCount, customerName, customerMeta,
  onInc, onDec, onRemove, onPay, onDiscount, onPark, onOpenParked, onClear,
  onCustomer, onDropCustomer, paying,
}: {
  lines: CartLine[];
  totals: Totals;
  invoiceLabel: string;
  parkedCount: number;
  customerName: string | null;
  customerMeta: string | null;
  onInc: (variantId: string) => void;
  onDec: (variantId: string) => void;
  onRemove: (variantId: string) => void;
  onPay: () => void;
  onDiscount: () => void;
  onPark: () => void;
  onOpenParked: () => void;
  onClear: () => void;
  onCustomer: () => void;
  onDropCustomer: () => void;
  paying: boolean;
}) {
  /*
   * Selling something the system thinks is gone is allowed, and said out loud.
   *
   * The customer is holding it. Refusing the sale over a counting error loses
   * real money to protect a number that is already wrong — so the till sells it
   * and flags the row for the manager instead.
   */
  const oversold = lines.filter((l) => soldPastZero(l.item, l.quantity));

  return (
    <div
      style={{
        width: 428, flex: 'none', background: 'var(--luma-card)',
        borderInlineStart: '1px solid var(--luma-line)',
        display: 'flex', flexDirection: 'column', minHeight: 0,
      }}
    >
      <div
        style={{
          flex: 'none', padding: '12px 14px', borderBottom: '1px solid var(--pos-rule)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
            فاکتور جاری
          </div>
          <div style={{ fontWeight: 800, fontSize: 17 }}>{invoiceLabel}</div>
        </div>
        <button
          onClick={onPark}
          disabled={lines.length === 0}
          style={{
            height: 44, padding: '0 12px', borderRadius: 10,
            border: '1px solid var(--luma-line-strong)', fontSize: 13, fontWeight: 700,
            color: lines.length === 0 ? 'var(--pos-disabled-ink)' : 'var(--pos-ink-dark)',
          }}
        >
          پارک
        </button>
        <button
          onClick={onOpenParked}
          style={{
            height: 44, padding: '0 12px', borderRadius: 10,
            border: '1px solid var(--luma-line-strong)', fontSize: 13, fontWeight: 700,
            color: 'var(--pos-ink-dark)',
          }}
        >
          پارک‌شده‌ها {fa(parkedCount)}
        </button>
      </div>

      {/* --- the customer -------------------------------------------------- */}
      {customerName === null ? (
        <button
          onClick={onCustomer}
          style={{
            flex: 'none', display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderBottom: '1px solid var(--pos-rule)',
            textAlign: 'right', minHeight: 52, width: '100%',
          }}
        >
          <div
            style={{
              width: 30, height: 30, borderRadius: '50%', background: 'var(--luma-surface-hover)',
              display: 'grid', placeItems: 'center', fontSize: 14, color: 'var(--luma-muted)',
            }}
          >
            ☺
          </div>
          <div style={{ flex: 1, lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>مشتری: مهمان</div>
            <div style={{ fontSize: 11.5, color: 'var(--luma-placeholder)' }}>
              شماره موبایل را بزنید تا فاکتور الکترونیکی ارسال شود
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-accent-hover)' }}>افزودن</div>
        </button>
      ) : (
        <div
          style={{
            flex: 'none', display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderBottom: '1px solid var(--pos-rule)',
            minHeight: 52, background: 'var(--luma-tint-warning-bg)',
          }}
        >
          <div
            style={{
              width: 30, height: 30, borderRadius: '50%', background: 'var(--pos-accent-deeper)',
              display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
              color: 'var(--luma-accent-tint-text)',
            }}
          >
            ☺
          </div>
          <div style={{ flex: 1, lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{customerName}</div>
            <div style={{ fontSize: 11.5, color: '#8A6A4A' }}>{customerMeta}</div>
          </div>
          <button
            onClick={onDropCustomer}
            style={{
              minHeight: 44, minWidth: 44, display: 'grid', placeItems: 'center',
              borderRadius: 9, fontSize: 13, fontWeight: 700, color: 'var(--luma-placeholder)',
            }}
          >
            حذف
          </button>
        </div>
      )}

      {oversold.length > 0 && (
        <div
          style={{
            flex: 'none', display: 'flex', gap: 9, alignItems: 'flex-start',
            padding: '10px 14px', background: 'var(--luma-tint-warning-bg)',
            borderBottom: '1px solid var(--luma-tint-warning-border)',
          }}
        >
          <div
            style={{
              width: 20, height: 20, flex: 'none', borderRadius: 6,
              background: 'var(--pos-accent-deep)', display: 'grid', placeItems: 'center',
              color: 'var(--luma-accent-tint-text)', fontSize: 12, fontWeight: 800,
            }}
          >
            !
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--luma-accent-tint-text)', lineHeight: 1.55 }}>
            {oversold.map((l) => l.item.name).join('، ')} موجودی کافی ندارد — فروش با کسری مجاز است و در پنل علامت می‌خورد.
          </div>
        </div>
      )}

      {/* --- the lines ----------------------------------------------------- */}
      {lines.length === 0 ? (
        <div
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 10, padding: 24, textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 56, height: 56, borderRadius: 14, background: 'var(--luma-surface)',
              border: '1px solid var(--pos-rule-strong)', display: 'grid', placeItems: 'center',
              fontSize: 22, color: 'var(--pos-dark-ink)',
            }}
          >
            ▤
          </div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>سبد خالی است</div>
          <div style={{ fontSize: 13, color: 'var(--luma-muted)', maxWidth: '30ch' }}>
            بارکد کالا را اسکن کنید یا از سبد کالا انتخاب کنید. فروش قبلی با «پارک‌شده‌ها» برمی‌گردد.
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {lines.map((l) => (
            <div
              key={l.item.variantId}
              style={{
                padding: '11px 14px', borderBottom: '1px solid var(--pos-rule-faint)',
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.35 }}>{l.item.name}</div>
                <div className="pos-num" style={{ fontSize: 12, color: 'var(--luma-muted)', marginTop: 1 }}>
                  {toman(l.item.priceRial)} × {qty(l.quantity)}
                </div>
                {l.discountRial > 0 && (
                  <div
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5,
                      background: 'var(--luma-accent-tint)', border: '1px solid var(--luma-accent-tint-border)',
                      color: 'var(--luma-accent-tint-text)', borderRadius: 7, padding: '2px 7px',
                      fontSize: 11.5, fontWeight: 700,
                    }}
                  >
                    تخفیف ردیف {toman(l.discountRial)}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: 'var(--luma-surface)', border: '1px solid var(--pos-rule-strong)',
                  borderRadius: 10, padding: 2,
                }}
              >
                <button
                  onClick={() => onDec(l.item.variantId)}
                  aria-label={`کم کردن ${l.item.name}`}
                  style={{ width: 44, height: 44, borderRadius: 8, fontSize: 19, fontWeight: 700, color: 'var(--pos-ink-dark)' }}
                >
                  −
                </button>
                <div className="pos-num" style={{ minWidth: 38, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>
                  {qty(l.quantity)}
                </div>
                <button
                  onClick={() => onInc(l.item.variantId)}
                  aria-label={`اضافه کردن ${l.item.name}`}
                  style={{ width: 44, height: 44, borderRadius: 8, fontSize: 19, fontWeight: 700, color: 'var(--pos-ink-dark)' }}
                >
                  +
                </button>
              </div>

              <div style={{ width: 110, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                <div className="pos-num" style={{ fontWeight: 700, fontSize: 15 }}>
                  {toman(Math.round(l.item.priceRial * l.quantity) - l.discountRial)}
                </div>
                <button
                  onClick={() => onRemove(l.item.variantId)}
                  aria-label={`حذف ${l.item.name} از فاکتور`}
                  style={{
                    minHeight: 44, minWidth: 44, padding: '0 8px', fontSize: 12.5,
                    fontWeight: 600, color: 'var(--luma-placeholder)', borderRadius: 8, textAlign: 'left',
                  }}
                >
                  حذف
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- totals -------------------------------------------------------- */}
      <div
        style={{
          flex: 'none', borderTop: '1px solid var(--pos-rule)',
          padding: '12px 14px 14px', background: 'var(--luma-sunken)',
        }}
      >
        <Row label={`جمع کالاها (${fa(totals.count)})`} value={toman(totals.subtotalRial)} />
        <Row
          label="تخفیف فاکتور"
          value={`−${toman(totals.lineDiscountRial + totals.basketDiscountRial)}`}
          tone="var(--luma-accent-tint-text)"
        />
        <Row label="مالیات ارزش افزوده" value={toman(totals.vatRial)} />

        <div style={{ height: 1, background: 'var(--pos-rule-strong)', margin: '10px 0' }} />

        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--luma-ink-2)' }}>مبلغ قابل پرداخت</span>
          <span
            className="pos-num"
            style={{ fontWeight: 800, fontSize: 34, lineHeight: 1, letterSpacing: '-.01em' }}
          >
            {toman(totals.totalRial)}
          </span>
        </div>

        <button
          onClick={onPay}
          disabled={lines.length === 0 || paying}
          style={{
            width: '100%', height: 60, marginTop: 12, borderRadius: 12,
            background: lines.length && !paying ? 'var(--luma-accent)' : 'var(--pos-disabled)',
            color: '#FFF', fontWeight: 800, fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 18px', boxShadow: '0 3px 10px rgba(249,124,21,.26)',
          }}
        >
          <span>پرداخت</span>
          <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>Enter</span>
        </button>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            onClick={onDiscount}
            disabled={lines.length === 0}
            style={{
              flex: 1, height: 48, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
              background: 'var(--luma-card)', fontSize: 13.5, fontWeight: 700,
              color: lines.length === 0 ? 'var(--pos-disabled-ink)' : undefined,
            }}
          >
            تخفیف فاکتور
          </button>
          <button
            onClick={onPark}
            disabled={lines.length === 0}
            style={{
              flex: 1, height: 48, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
              background: 'var(--luma-card)', fontSize: 13.5, fontWeight: 700,
              color: lines.length === 0 ? 'var(--pos-disabled-ink)' : undefined,
            }}
          >
            پارک فروش
          </button>
          <button
            onClick={onClear}
            disabled={lines.length === 0}
            style={{
              width: 96, height: 48, borderRadius: 10, border: '1px solid var(--pos-danger-line)',
              background: 'var(--luma-card)', color: 'var(--luma-danger)',
              fontSize: 13.5, fontWeight: 700,
            }}
          >
            لغو سبد
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', fontSize: 13.5,
        color: 'var(--luma-ink-2)', padding: '2px 0',
      }}
    >
      <span>{label}</span>
      <span className="pos-num" style={{ fontWeight: 600, color: tone ?? 'var(--luma-ink)' }}>{value}</span>
    </div>
  );
}
